import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Plans.module.css';
import Card from '../components/Card';
import Button from '../components/Button';
import Skeleton from '../components/Skeleton';
import { usePlans, useSubscription, useTrialStatus } from '../hooks';
import { paymentService } from '../services/api';
import { useToast } from '../store/ToastContext';
import { formatCurrency, formatDate, getDaysRemaining, GST_RATE, calculateGst } from '../utils/helpers';
import { CheckIcon, MinusIcon, PlusIcon } from '../components/Icons';

const FAIR_USAGE_NOTE = 'Fair usage applies. Contact us if you expect very high signing volumes.';


function StarIcon({ size = 12, ...props }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  );
}

function licenceRangeLabel(plan) {
  const min = plan.min_dongles || 1;
  const max = plan.max_dongles || 10;
  if (min === max) return `${min} licence${min > 1 ? 's' : ''}`;
  if (max >= 500) return `${min}+ licences`;
  return `${min}–${max} licences`;
}

function Plans() {
  const navigate = useNavigate();
  const toast = useToast();

  const { plans, loading } = usePlans();
  const { subscription } = useSubscription();
  const { trial } = useTrialStatus();

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [dongleCount, setDongleCount] = useState(1);
  const [startingTrial, setStartingTrial] = useState(false);
  const [groupTab, setGroupTab] = useState(0);
  const calculatorRef = useRef(null);

  const isOnTrial = !!subscription?.is_trial;
  const trialActive = trial.state === 'active';
  const trialExpired = trial.state === 'expired';
  const hasSubscription = !!subscription;
  const currentDaysLeft = subscription?.end_date ? getDaysRemaining(subscription.end_date) : 0;
  const currentExpired = !!subscription && (
    currentDaysLeft === 0 || (subscription.status || '').toLowerCase() === 'expired'
  );

  // If the current plan is Business or Enterprise, open that tab by default
  const currentPlanId = !isOnTrial ? subscription?.plan_id : null;
  useEffect(() => {
    if (!currentPlanId) return;
    const sorted = plans
      .filter(p => !/trial/i.test(p.name || '') && (parseFloat(p.price) || 0) > 0)
      .sort((x, y) => (x.min_dongles || 1) - (y.min_dongles || 1));
    const tabIndex = sorted.slice(-2).findIndex(p => p.id === currentPlanId);
    if (sorted.length > 3 && tabIndex >= 0) setGroupTab(tabIndex);
  }, [plans, currentPlanId]);

  // Scroll the configurator into view once a plan is selected
  useEffect(() => {
    if (selectedPlan && calculatorRef.current) {
      calculatorRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [selectedPlan]);

  // Basic & Pro get their own cards; the higher tiers (Business, Enterprise)
  // share the last card and are switched with tabs so everything fits in one row.
  // The backend also returns the free trial as a plan; it gets its own card, so keep it
  // out of the paid list to avoid showing two trials.
  const isTrialPlan = (p) => /trial/i.test(p.name || '') || (parseFloat(p.price) || 0) === 0;
  const trialPlan = plans.find(isTrialPlan);
  const sortedPlans = plans
    .filter(p => !isTrialPlan(p))
    .sort((a, b) => (a.min_dongles || 1) - (b.min_dongles || 1));
  const tabbedPlans = sortedPlans.length > 3 ? sortedPlans.slice(-2) : [];
  const standalonePlans = sortedPlans.slice(0, sortedPlans.length - tabbedPlans.length);

  // Features shared by every paid plan = the core product the trial includes
  const coreFeatures = (sortedPlans[0]?.features || []).filter(f =>
    sortedPlans.every(p => (p.features || []).includes(f))
  );
  const trialDays = trialPlan?.duration_days;
  const trialFeatures = trialPlan?.features?.length
    ? trialPlan.features
    : [...coreFeatures, '1 device licence'];

  const handleSelectPlan = (plan) => {
    const tabIndex = tabbedPlans.findIndex(p => p.id === plan.id);
    if (tabIndex >= 0) setGroupTab(tabIndex);
    setSelectedPlan(plan);
    const min = plan.min_dongles || 1;
    const max = plan.max_dongles || 10;
    setDongleCount(prev => Math.min(Math.max(prev, min), max));
  };

  const minDongles = selectedPlan?.min_dongles || 1;
  const maxDongles = selectedPlan?.max_dongles || 10;

  const clampCount = (value) => Math.max(minDongles, Math.min(maxDongles, value));

  const handleDongleChange = (delta) => setDongleCount(prev => clampCount(prev + delta));

  const handleDongleInput = (e) => {
    const value = parseInt(e.target.value, 10);
    setDongleCount(Number.isNaN(value) ? minDongles : clampCount(value));
  };

  // Suggest the right plan when the licence count is outside the selected plan's range
  const nextPlan = selectedPlan
    ? plans.find(p => (p.min_dongles || 1) === maxDongles + 1)
    : null;

  const pricePerLicence = parseFloat(selectedPlan?.price) || 0;
  const total = pricePerLicence * dongleCount;

  const handleStartTrial = async () => {
    setStartingTrial(true);
    try {
      const response = await paymentService.startTrial();
      if (response.data.success) {
        toast.success('Your free trial has started');
        navigate('/dashboard');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.message || 'Unable to start free trial');
    } finally {
      setStartingTrial(false);
    }
  };

  const handleProceedToCheckout = () => {
    if (!selectedPlan) return;
    navigate('/checkout', {
      state: {
        plan: selectedPlan,
        dongleCount,
        priceBreakdown: {
          base_price: pricePerLicence,
          dongle_price: pricePerLicence * (dongleCount - 1),
          total,
        },
      },
    });
  };

  // Start / expiry / days-left / licences box, used for the trial and the current plan
  const renderStats = ({ startDate, endDate, daysRemaining, totalDays, licences, expired }) => {
    const expiringSoon = !expired && daysRemaining <= 30;
    const daysClass = expired ? styles.expiredText : expiringSoon ? styles.warningText : undefined;
    return (
      <dl className={styles.trialStats}>
        <div>
          <dt>Started</dt>
          <dd>{startDate ? formatDate(startDate) : '—'}</dd>
        </div>
        <div>
          <dt>{expired ? 'Expired on' : 'Expires on'}</dt>
          <dd className={expired ? styles.expiredText : undefined}>
            {endDate ? formatDate(endDate) : '—'}
          </dd>
        </div>
        <div>
          <dt>Days left</dt>
          <dd className={daysClass}>
            {expired ? 0 : daysRemaining}
            {totalDays ? <span className={styles.ofTotal}> / {totalDays}</span> : null}
          </dd>
        </div>
        <div>
          <dt>Licences</dt>
          <dd>{licences}</dd>
        </div>
      </dl>
    );
  };

  const renderPlanCard = (plan, tabs = null) => {
    const isSelected = selectedPlan?.id === plan.id;
    const isCurrent = !isOnTrial && subscription?.plan_id === plan.id;
    return (
      <Card
        key={tabs ? 'tabbed' : plan.id}
        padding="none"
        className={[
          styles.planCard,
          plan.popular && styles.popularCard,
          isCurrent && styles.currentCard,
          isSelected && styles.selected,
        ].filter(Boolean).join(' ')}
        onClick={() => handleSelectPlan(plan)}
      >
        <div className={styles.planCardInner}>
          {plan.popular && (
            <span className={styles.popularBadge}>
              <StarIcon size={10} /> Most Popular
            </span>
          )}
          {isCurrent && (
            <span className={currentExpired ? styles.statusExpired : styles.currentBadge}>
              {currentExpired ? 'Expired' : 'Current plan'}
            </span>
          )}

          {tabs}

          <h3 className={styles.planName}>{plan.name}</h3>
          <p className={styles.planLicences}>{licenceRangeLabel(plan)}</p>

          <div className={styles.planPrice}>
            <span className={styles.priceAmount}>
              {formatCurrency(plan.price)}
              <StarIcon className={styles.priceStar} title="Fair usage applies" />
            </span>
            <span className={styles.pricePeriod}>per licence / year</span>
          </div>

          {isCurrent && renderStats({
            startDate: subscription.start_date,
            endDate: subscription.end_date,
            daysRemaining: currentDaysLeft,
            totalDays: subscription.total_days,
            licences: subscription.number_of_dongles || subscription.dongle_count || 1,
            expired: currentExpired,
          })}

          <ul className={styles.featuresList} key={plan.id}>
            {(plan.features || []).map((feature, index) => (
              <li
                key={index}
                className={`${styles.featureItem} ${coreFeatures.includes(feature) ? '' : styles.featureExtra}`}
              >
                <CheckIcon className={styles.featureIcon} />
                <span>{feature}</span>
              </li>
            ))}
          </ul>

          <Button
            variant={plan.popular || isSelected ? 'primary' : 'secondary'}
            fullWidth
            className={styles.planButton}
            leftIcon={isSelected ? <CheckIcon /> : null}
          >
            {isSelected ? 'Selected' : isCurrent ? `Renew ${plan.name}` : `Choose ${plan.name}`}
          </Button>

          <p className={styles.footnote}>
            <StarIcon className={styles.footnoteIcon} />
            <span>{FAIR_USAGE_NOTE}</span>
          </p>
        </div>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <Skeleton variant="rectangular" height={24} width={180} />
        </div>
        <div className={styles.plansGrid}>
          {[1, 2, 3, 4].map(i => (
            <Skeleton.Card key={i} lines={5} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h1 className={styles.title}>Simple annual pricing by licence volume</h1>
        <p className={styles.subtitle}>
          Pick the band that matches the number of Windows systems that will use BulkSigner.
        </p>
        <p className={styles.billingNote}>Annual billing · Price per device licence · Taxes extra</p>
      </header>

      {/* Free Trial, Basic, Pro and Business/Enterprise (tabbed) in one row */}
      <div id="plans-grid" className={styles.plansGrid}>
        {/* Free Trial card */}
        <Card
          padding="none"
          className={`${styles.planCard} ${styles.trialCard} ${trialExpired ? styles.trialCardExpired : ''}`}
        >
          <div className={styles.planCardInner}>
            <span className={styles.trialBadge}>Free</span>
            {trialActive && <span className={styles.statusActive}>Active</span>}
            {trialExpired && <span className={styles.statusExpired}>Expired</span>}

            <h3 className={styles.planName}>Free Trial</h3>
            <p className={styles.planLicences}>1 licence</p>

            <div className={styles.planPrice}>
              <span className={`${styles.priceAmount} ${styles.trialAmount}`}>₹0</span>
              <span className={styles.pricePeriod}>
                {trialDays ? `for ${trialDays} day${trialDays > 1 ? 's' : ''} · ` : ''}no payment required
              </span>
            </div>

            {trial.state !== 'none' && renderStats({
              startDate: trial.startDate,
              endDate: trial.endDate,
              daysRemaining: trial.daysRemaining,
              totalDays: trial.totalDays,
              licences: trial.licences,
              expired: trialExpired,
            })}

            <ul className={styles.featuresList}>
              {trialFeatures.map(feature => (
                <li key={feature} className={styles.featureItem}>
                  <CheckIcon className={`${styles.featureIcon} ${styles.trialFeatureIcon}`} />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            {trialActive || isOnTrial ? (
              <Button variant="secondary" fullWidth disabled className={styles.planButton}>
                Trial in progress
              </Button>
            ) : trialExpired ? (
              <Button variant="secondary" fullWidth disabled className={styles.planButton}>
                {trial.upgraded ? 'Upgraded' : 'Trial ended'}
              </Button>
            ) : hasSubscription ? (
              <Button variant="secondary" fullWidth disabled className={styles.planButton}>
                You already have a plan
              </Button>
            ) : (
              <Button
                variant="success"
                fullWidth
                loading={startingTrial}
                onClick={handleStartTrial}
                className={styles.planButton}
              >
                Start Free Trial
              </Button>
            )}

            <p className={styles.footnote}>
              <StarIcon className={styles.footnoteIcon} />
              <span>
                {trialExpired && !trial.upgraded
                  ? 'Your trial has ended. Choose a plan to keep signing.'
                  : 'One free trial per account. Upgrade anytime.'}
              </span>
            </p>
          </div>
        </Card>

        {standalonePlans.map(plan => renderPlanCard(plan))}

        {tabbedPlans.length > 0 && renderPlanCard(tabbedPlans[groupTab] || tabbedPlans[0], (
          <div className={styles.planTabs} role="tablist" aria-label="Choose plan">
            {tabbedPlans.map((p, i) => (
              <button
                key={p.id}
                type="button"
                role="tab"
                aria-selected={groupTab === i}
                className={`${styles.planTab} ${groupTab === i ? styles.planTabActive : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setGroupTab(i);
                }}
              >
                {p.name}
              </button>
            ))}
          </div>
        ))}
      </div>

      <p className={styles.bandNote}>
        1–4 licences are billed as individual Basic licences. Taxes are additional. Final commercial
        terms are subject to order confirmation.
      </p>

      {/* Configurator - shown only after a plan is selected */}
      {selectedPlan && (
        <Card ref={calculatorRef} padding="none" className={styles.calculatorCard}>
          <div className={styles.calculatorInner}>
            <div>
              <h3 className={styles.calculatorTitle}>Configure your {selectedPlan.name} plan</h3>
              <p className={styles.dongleLabelHint}>
                {selectedPlan.name} covers {licenceRangeLabel(selectedPlan)} at{' '}
                {formatCurrency(pricePerLicence)} per licence / year.
              </p>
            </div>

            <div className={styles.dongleSelector}>
              <label className={styles.dongleLabelText} htmlFor="licence-count">
                Number of licences
              </label>
              <div className={styles.dongleControls}>
                <button
                  type="button"
                  className={styles.dongleButton}
                  onClick={() => handleDongleChange(-1)}
                  disabled={dongleCount <= minDongles}
                  aria-label="Decrease licences"
                >
                  <MinusIcon />
                </button>
                <input
                  id="licence-count"
                  type="number"
                  className={styles.dongleInput}
                  value={dongleCount}
                  min={minDongles}
                  max={maxDongles}
                  onChange={handleDongleInput}
                />
                <button
                  type="button"
                  className={styles.dongleButton}
                  onClick={() => handleDongleChange(1)}
                  disabled={dongleCount >= maxDongles}
                  aria-label="Increase licences"
                >
                  <PlusIcon />
                </button>
                <span className={styles.dongleLabelHint}>Min {minDongles}, max {maxDongles}</span>
              </div>
              {dongleCount >= maxDongles && nextPlan && (
                <button
                  type="button"
                  className={styles.upgradeHint}
                  onClick={() => handleSelectPlan(nextPlan)}
                >
                  Need more than {maxDongles}? Switch to {nextPlan.name} ({licenceRangeLabel(nextPlan)}) at{' '}
                  {formatCurrency(nextPlan.price)} per licence →
                </button>
              )}
            </div>

            <div className={styles.priceBreakdown}>
              <div className={styles.priceRow}>
                <span>Price per licence / year</span>
                <span>{formatCurrency(pricePerLicence)}</span>
              </div>
              <div className={styles.priceRow}>
                <span>Licences</span>
                <span>× {dongleCount}</span>
              </div>
              <div className={styles.priceRow}>
                <span>Subtotal</span>
                <span>{formatCurrency(total)}</span>
              </div>
              <div className={styles.priceRow}>
                <span>GST ({GST_RATE}%)</span>
                <span>{formatCurrency(calculateGst(total).gst)}</span>
              </div>
              <div className={`${styles.priceRow} ${styles.totalRow}`}>
                <span>Annual total <small className={styles.taxNote}>(incl. GST)</small></span>
                <span className={styles.totalAmount}>{formatCurrency(calculateGst(total).total)}</span>
              </div>
            </div>

            <Button fullWidth size="lg" onClick={handleProceedToCheckout}>
              Proceed to Checkout
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

export default Plans;
